import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import MarkerClusterer from '@google/markerclustererplus';

// types
import { THEME_NAMES, type ThemeNameProps } from 'themes/theme.types';

// utils
import { buildMarker } from './createMarkerBuilder';
import { useThemeName } from 'utils/hooks/use-theme';
import { encodeValue } from 'utils/base64';
import { isEmpty } from 'utils/is-empty';

type MarkerClusterProps = {
  mode: string;
  categories;
  handleDetail: (...args: any[]) => void;
  autoCoordinates: boolean;
  mapRef?: google.maps.Map | null;
  filteredData?: any[];
  googleMapsRef?: typeof google.maps | null;
  startLat?: number;
  startLng?: number;
  isNord?: boolean;
};

const styles = {
  anchorText: [-12, 0],
  height: 60,
  width: 60,
  url: `/images/marker/marker-cluster.svg`,
};

export function useMarkerCluster({
  mapRef,
  filteredData,
  mode,
  categories,
  handleDetail,
  isNord = false,
  googleMapsRef,
  startLat,
  startLng,
  autoCoordinates = false,
}: Readonly<MarkerClusterProps>) {
  const [markerCluster, setMarkerCluster] = useState<any>();
  const markers = useRef<any>([]);
  const themeName = useThemeName() ?? THEME_NAMES.DEFAULT;

  const gddsStyles = useMemo(
    () => ({
      anchorText: [0, 0],
      height: 28,
      width: 28,
      url: getSVGUrl(themeName),
    }),
    [themeName],
  );

  /**
   * Initializes a new marker cluster by setting the markers on the map and
   * using them in the initialization call.
   */
  const setMarkers = useCallback(() => {
    if (!filteredData || isEmpty(mapRef)) {
      return;
    }

    markers.current = filteredData.map((item) => {
      const marker = buildMarker({
        isNord,
        categories,
        maps: googleMapsRef,
        mode,
        ...item,
        draggable: false,
      });
      marker.addListener('click', () => {
        handleDetail(item);
      });

      return marker;
    });
  }, [categories, filteredData, googleMapsRef, handleDetail, isNord, mapRef, mode]);

  /**
   * Initialization of the MarkerClusterer. Uses @google/markerclustererplus.
   */
  useEffect(() => {
    if (mapRef) {
      setMarkerCluster(
        new MarkerClusterer(mapRef, markers.current, {
          maxZoom: null,
          gridSize: 60,
          ignoreHidden: false,
          batchSize: 100,
          zoomOnClick: true,
          styles: [
            {
              textColor: 'white',
              fontFamily: 'inherit',
              textSize: 12,
              ...(isNord ? gddsStyles : styles),
            },
          ],
          calculator: calculateMarkerIcon,
        }),
      );
    }
  }, [gddsStyles, isNord, mapRef]);

  useEffect(() => {
    if (!isEmpty(filteredData) && !isEmpty(mapRef) && markerCluster) {
      markerCluster.removeMarkers(markers.current, true);
      markers.current = [];
      setMarkers();
      markerCluster.addMarkers(markers.current, true);
      markerCluster.repaint();
      if (autoCoordinates) {
        markerCluster.fitMapToMarkers();
      }
    } else if (!isEmpty(mapRef) && markerCluster) {
      markerCluster.removeMarkers(markers.current, true);
      markers.current = [];
      markerCluster.repaint();

      if (!autoCoordinates && startLat && startLng && googleMapsRef) {
        mapRef.panTo(new googleMapsRef.LatLng(startLat, startLng));
      }
    }
  }, [
    markerCluster,
    filteredData,
    categories,
    autoCoordinates,
    googleMapsRef,
    mapRef,
    setMarkers,
    startLat,
    startLng,
  ]);
}

function getSVGUrl(themeName: ThemeNameProps) {
  const SVG_URL = {
    DEFAULT: `data:image/svg+xml;base64,${svg('#26513B')}`,
    NORD: `data:image/svg+xml;base64,${svg('#26513B')}`,
    KOLO: `data:image/svg+xml;base64,${svg('#1E818A')}`,
    TWYFORD: `data:image/svg+xml;base64,${svg('#245C4E')}`,
  };

  if (themeName === THEME_NAMES.NORD) return SVG_URL.NORD;
  if (themeName === THEME_NAMES.KOLO) return SVG_URL.KOLO;
  if (themeName === THEME_NAMES.TWYFORD) return SVG_URL.TWYFORD;

  return SVG_URL.DEFAULT;
}

function svg(color: string) {
  return encodeValue(`
    <svg  version="1.1" id="Ebene_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="28px" height="28px" viewBox="0 0 60 60" enable-background="new 0 0 60 60" xml:space="preserve">
      <ellipse ry="28.5" rx="28.5" id="svg_4" cy="30.750006" cx="30.125004" stroke-width="3" fill="${color}"/>
    </svg>`);
}

function calculateMarkerIcon(markers) {
  return {
    text: markers.length > 9 ? '9+' : markers.length.toString(),
    title: '',
    index: 1,
  };
}
